<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Centro de Custo</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Consulte, filtre e administre os centros cadastrados.</p>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">Centro de Custo</label>
              <input
                v-model="filtroNome"
                type="text"
                placeholder="Centro de Custo..."
                class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">ID</label>
              <input
                v-model="filtroId"
                type="text"
                placeholder="ID..."
                class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">Status</label>

              <div class="relative group">
                <select
                  v-model="filtroStatus"
                  id="status-select"
                  class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
                >
                  <option value="">Todos</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>

                <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div class="flex items-end gap-2 md:col-span-4 lg:col-span-1">
              <button
                type="button"
                class="h-11 w-full rounded-xl font-medium text-white transition"
                :style="{ backgroundColor: primaryColor }"
                @click="aplicarFiltros"
                @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                @mouseleave="$event.target.style.backgroundColor = primaryColor"
              >
                Buscar
              </button>

              <button type="button" class="h-11 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50" @click="limparFiltros">Limpar</button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <router-link
          to="/centros/form"
          class="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm transition hover:shadow-md sm:w-auto"
          v-if="!$store.state.user.centrocusto"
        >
          <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <span class="text-sm font-medium text-gray-700">Novo Centro de Custo</span>
        </router-link>
      </div>

      <div class="w-full overflow-hidden sm:rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Centro de Custo</th>
                <th class="px-6 py-4 text-left font-medium">ID</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
                <th class="px-6 py-4 text-center font-medium">Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in centrosPaginados" :key="item._id" class="border-b border-gray-100 transition hover:bg-slate-50">
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                      {{ (item.nome || "").slice(0, 2).toUpperCase() }}
                    </div>
                    <div class="flex flex-col">
                      <span class="font-medium text-gray-800">{{ item.nome }}</span>
                      <span class="text-xs text-gray-400">Centro cadastrado</span>
                    </div>
                  </div>
                </td>

                <td class="px-6 font-medium text-gray-700">
                  {{ item._id || "-" }}
                </td>

                <td class="px-6 text-center">
                  <span class="rounded-full px-3 py-1 text-xs font-medium" :class="item.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                    {{ item.ativo ? "Ativo" : "Inativo" }}
                  </span>
                </td>

                <td class="px-6 py-5">
                  <div class="flex flex-wrap justify-center gap-2">
                    <router-link
                      :to="`/centros/form/${item._id}`"
                      class="rounded-lg px-4 py-2 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: primaryColor }"
                      @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                      @mouseleave="$event.target.style.backgroundColor = primaryColor"
                    >
                      Editar
                    </router-link>

                    <button v-if="!$store.state.user.centrocusto" type="button" class="rounded-lg px-4 py-2 text-sm font-medium text-white bg-red-700 hover:bg-red-800 transition" @click="remove(item._id)">Remover</button>
                  </div>
                </td>
              </tr>

              <tr v-if="centrosPaginados.length === 0">
                <td colspan="4" class="px-6 py-12 text-center text-sm text-gray-400">Nenhum centro encontrado com os filtros atuais.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        :current-page="paginaAtual"
        @update:currentPage="paginaAtual = $event"
        :total-pages="totalPaginas"
        :total-items="centrosFiltrados.length"
        :page-size="itensPorPagina"
        item-label="registros"
        @paginate="setPage"
      />
    </div>
  </div>
</template>

<script>
import AppPagination from "@/components/AppPagination.vue";
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  components: {
    AppPagination
  },
  data() {
    return {
      filtroNome: "",
      filtroId: "",
      filtroStatus: "",
      filtrosAplicados: {
        nome: "",
        id: "",
        status: ""
      },
      paginaAtual: 1,
      itensPorPagina: 5,
      centros: []
    };
  },
  computed: {
    centrosFiltrados() {
      return this.centros.filter((item) => {
        const nome = String(item.nome || "").toLowerCase();
        const id = String(item.idCustom || item._id || "").toLowerCase();
        const status = item.ativo ? "Ativo" : "Inativo";

        const matchNome = nome.includes(this.filtrosAplicados.nome.toLowerCase());
        const matchId = id.includes(this.filtrosAplicados.id.toLowerCase());
        const matchStatus = !this.filtrosAplicados.status || status === this.filtrosAplicados.status;

        return matchNome && matchId && matchStatus;
      });
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.centrosFiltrados.length / this.itensPorPagina));
    },
    centrosPaginados() {
      const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
      return this.centrosFiltrados.slice(inicio, inicio + this.itensPorPagina);
    }
  },
  methods: {
    aplicarFiltros() {
      this.filtrosAplicados = {
        nome: this.filtroNome.trim(),
        id: this.filtroId.trim(),
        status: this.filtroStatus
      };
      this.paginaAtual = 1;
    },
    limparFiltros() {
      this.filtroNome = "";
      this.filtroId = "";
      this.filtroStatus = "";
      this.filtrosAplicados = {
        nome: "",
        id: "",
        status: ""
      };
      this.paginaAtual = 1;
    },
    setPage(page) {
      this.paginaAtual = page;
    },
    async carregarCentros() {
      const response = await this.$http.post("/v1/centrocusto/list", { all: true });
      this.centros = response?.data?.data || [];
      this.paginaAtual = 1;
    },
    async remove(id) {
      if (!confirm("Tem certeza que deseja excluir esse centro de custo?")) {
        return;
      }

      const resp = await this.$http.delete(`/v1/centrocusto/${id}`);
      if (!resp.data.success) {
        this.$vToastify.error(resp.data.err);
        return;
      }

      this.$vToastify.success("Centro removido");
      await this.carregarCentros();
    }
  },
  async beforeMount() {
    await this.carregarCentros();
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

#status-select::-ms-expand {
  display: none;
}

#status-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
